import React, { useState, useEffect } from 'react';
import { COVER_BG_IMAGE } from '../constants/cover';
import { useLanguage } from '../context/LanguageContext';
import { mediaAPI } from '../services/api';
import { FaImages, FaVideo, FaDownload, FaExpandAlt, FaTv, FaMicrophone, FaNewspaper, FaCog } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const MediaPage = () => {
    const { t, language } = useLanguage();
    const [activeTab, setActiveTab] = useState('photos');
    const [mediaItems, setMediaItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const isAdmin = !!localStorage.getItem('token');

    useEffect(() => {
        const loadMedia = async () => {
            try {
                setLoading(true);
                const response = await mediaAPI.getAll('all');
                setMediaItems(response.data || []);
            } catch (error) {
                console.error('Error loading media:', error);
            } finally {
                setLoading(false);
            }
        };
        loadMedia();
    }, []);

    const filterMedia = (type) => mediaItems.filter(item => item.type === type && item.is_active === 1);

    const photos = filterMedia('photo');
    const tvInterviews = filterMedia('tv');
    const radioInterviews = filterMedia('radio');
    const pressInterviews = filterMedia('press');
    const downloads = filterMedia('download');
    const videos = filterMedia('video');

    const renderCard = (item) => {
        const isVideo = item.type === 'video' || item.type === 'tv' || item.type === 'radio';
        const isPhoto = item.type === 'photo';
        const isPress = item.type === 'press';
        const isDownload = item.type === 'download';

        return (
            <div key={item.id} style={styles.mediaCard}>
                <div
                    style={styles.imageWrapper}
                    onClick={() => item.media_url ? window.open(item.media_url, '_blank') : (isPhoto && window.open(item.thumbnail_url, '_blank'))}
                >
                    <img
                        src={item.thumbnail_url || (isDownload ? 'https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?w=500&q=80' : 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&q=80')}
                        alt={language === 'ar' ? item.title_ar : item.title}
                        style={styles.image}
                    />

                    {isVideo && (
                        <div style={styles.playOverlay}>
                            <div style={item.type === 'radio' ? styles.micIcon : styles.playIcon}>
                                {item.type === 'radio' ? <FaMicrophone /> : '▶'}
                            </div>
                            {item.duration && <span style={styles.duration}>{item.duration}</span>}
                        </div>
                    )}

                    {(isPhoto || isPress || isDownload) && (
                        <div style={styles.overlay}>
                            {isPhoto && <FaExpandAlt style={styles.expandIcon} />}
                            {isPress && <FaNewspaper style={styles.expandIcon} />}
                            {isDownload && <FaDownload style={styles.expandIcon} />}
                        </div>
                    )}
                </div>
                <div style={styles.cardInfo}>
                    <h3 style={styles.cardTitle}>{language === 'ar' ? item.title_ar : item.title}</h3>
                </div>
            </div>
        );
    };

    return (
        <div style={styles.container}>
            <header style={styles.header}>
                <div style={styles.headerOverlay}>
                    <h1 style={styles.title}>{t('media')}</h1>
                    <p style={styles.subtitle}>{language === 'ar' ? 'تابع أهم اللحظات والأحداث الكروية للرابطة' : 'Suivez les moments forts et les événements de la ligue'}</p>
                </div>
            </header>

            <main style={styles.main}>
                {isAdmin && (
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <button
                            onClick={() => {
                                // On navigue vers le dashboard
                                // Pour ouvrir le bon onglet, ce serait idéal d'avoir le context
                                // ou on les redirige simplement vers le dashboard
                                navigate('/admin/dashboard');
                            }}
                            style={styles.manageBtn}
                        >
                            <FaCog /> {language === 'ar' ? 'إدارة الملتيميديا (لوحة التحكم)' : 'Gérer ce contenu (Tableau de bord)'}
                        </button>
                    </div>
                )}
                <div style={styles.tabContainer}>
                    <button onClick={() => setActiveTab('photos')} style={{ ...styles.tab, ...(activeTab === 'photos' ? styles.activeTab : {}) }}><FaImages /> {t('gallery')}</button>
                    <button onClick={() => setActiveTab('tv')} style={{ ...styles.tab, ...(activeTab === 'tv' ? styles.activeTab : {}) }}><FaTv /> {t('tv')}</button>
                    <button onClick={() => setActiveTab('radio')} style={{ ...styles.tab, ...(activeTab === 'radio' ? styles.activeTab : {}) }}><FaMicrophone /> {t('radio')}</button>
                    <button onClick={() => setActiveTab('press')} style={{ ...styles.tab, ...(activeTab === 'press' ? styles.activeTab : {}) }}><FaNewspaper /> {t('press')}</button>
                    <button onClick={() => setActiveTab('downloads')} style={{ ...styles.tab, ...(activeTab === 'downloads' ? styles.activeTab : {}) }}><FaDownload /> {language === 'ar' ? 'التحميلات' : 'Téléchargements'}</button>
                </div>

                {loading ? (
                    <div style={styles.loading}>Chargement...</div>
                ) : (
                    <div style={styles.grid}>
                        {activeTab === 'photos' && photos.map(renderCard)}
                        {activeTab === 'tv' && tvInterviews.map(renderCard)}
                        {activeTab === 'radio' && radioInterviews.map(renderCard)}
                        {activeTab === 'press' && pressInterviews.map(renderCard)}
                        {activeTab === 'downloads' && (downloads.length > 0 ? downloads.map(renderCard) : (
                            <div style={styles.emptyState}>
                                <div style={styles.emptyIcon}><FaDownload /></div>
                                <h3>{language === 'ar' ? 'لا توجد مستندات للتحميل حالياً' : 'Aucun document disponible pour le moment'}</h3>
                            </div>
                        ))}
                        {activeTab === 'photos' && photos.length === 0 && !loading && (
                            <div style={styles.emptyState}>
                                <div style={styles.emptyIcon}><FaImages /></div>
                                <h3>{language === 'ar' ? 'لا توجد صور حالياً' : 'Aucune photo disponible pour le moment'}</h3>
                            </div>
                        )}
                        {/* On peut ajouter d'autres states vides si besoin */}
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

const styles = {
    container: { minHeight: '100vh', background: '#f8f9fa' },
    header: { height: '300px', backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.52) 0%, rgba(44, 82, 130, 0.48) 100%), url(${COVER_BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: 'white' },
    title: { fontSize: '48px', fontWeight: '900', marginBottom: '15px' },
    subtitle: { fontSize: '20px', opacity: 0.9, maxWidth: '700px', margin: '0 auto' },
    main: { maxWidth: '1300px', margin: '-50px auto 80px', background: 'white', borderRadius: '20px', padding: '40px', boxShadow: '0 10px 40px rgba(0,0,0,0.05)', position: 'relative', zIndex: 10 },
    tabContainer: { display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '50px', flexWrap: 'wrap' },
    tab: { padding: '12px 25px', borderRadius: '50px', border: '2px solid #eee', background: 'white', color: '#666', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.3s', fontSize: '14px' },
    activeTab: { background: '#1a365d', borderColor: '#1a365d', color: 'white', boxShadow: '0 8px 15px rgba(26, 54, 93, 0.3)' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '30px' },
    mediaCard: { borderRadius: '15px', overflow: 'hidden', boxShadow: '0 10px 20px rgba(0,0,0,0.05)', transition: 'transform 0.3s', background: 'white', cursor: 'pointer' },
    imageWrapper: { position: 'relative', height: '200px', overflow: 'hidden' },
    image: { width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' },
    overlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s' },
    expandIcon: { color: 'white', fontSize: '30px' },
    playOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    playIcon: { width: '60px', height: '60px', background: '#1a365d', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' },
    micIcon: { width: '60px', height: '60px', background: '#4facfe', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' },
    duration: { position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(0,0,0,0.8)', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' },
    cardInfo: { padding: '20px', borderTop: '1px solid #eee' },
    cardTitle: { margin: 0, fontSize: '18px', color: '#333' },
    emptyState: { textAlign: 'center', padding: '80px 20px', color: '#999', gridColumn: '1 / -1' },
    emptyIcon: { fontSize: '60px', marginBottom: '20px', opacity: 0.3 },
    loading: { textAlign: 'center', padding: '100px 0', fontSize: '18px', color: '#666' },
    manageBtn: { padding: '10px 20px', background: '#c41e3a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '15px', transition: 'background 0.3s' }
};

export default MediaPage;
